import React, { Fragment, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Theme,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { useTranslation } from '../../hooks/useTranslation';
import AccountDropdown from '../account-dropdown';
import Backdrop from '../backdrop';
import {
  AccountDetail,
  AccountType,
  PremiseInfo,
  TimeOfDayEnrollmentDetails,
  TimeOfDayEnrollmentStatus,
} from '../../__generated__/pge-types';
import { getPgeEnergyTrackerData } from '../account-summary/single-account/queries';
import colors from '../../themes/main-colors';
import useSelectedAccountParams from '../../hooks/useSelectedAccountParams';
import useAuthQuery from '../../hooks/useAuthQuery';
import useGetAccountType from '../../hooks/useGetAccountType';
import {
  GET_ACCOUNT_DETAILS,
  getTimeOfDayEnrollmentDetailsQuery,
} from '../my-programs/queries';
import { getActiveEncryptedSaId } from '../my-programs/utils';
import WidgetHost from '../cs-widgets/WidgetHost';
import useAccountCustomer from '../../hooks/useAccountCustomer';
import { useIsMobile } from '../../util/style-utils';
import { GET_TOD_ENROLLMENT_STATUS_QUERY } from './queries';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    heading: {
      fontSize: theme.spacing(4.5),
      lineHeight: theme.typography.pxToRem(28),
      fontWeight: 'bold',
      letterSpacing: '0.720px',
      fontFamily: 'Forma-DJR-Display',
      color: colors.lightCyan,
      [theme.breakpoints.down('sm')]: {
        fontSize: theme.spacing(3),
        marginBottom: '16px',
      },
    },
    colorTextPrimary: {
      backgroundColor: colors.navBarBackground,
    },
    widget: {
      backgroundColor: colors.lightGray2,
      margin: '20px',
      [theme.breakpoints.down('sm')]: {
        margin: '0px',
        padding: '0px',
      },
    },
    accountBox: {
      margin: '16px 0px 32px',
      [theme.breakpoints.down('sm')]: {
        margin: '16px 0px 20px',
      },
    },
    nonRes: {
      display: 'flex',
    },
    nonResText: {
      padding: '20px',
      fontSize: '20px',
      lineHeight: '25px',
      color: colors.lightCyan,
    },
    sectionTitle: {
      fontSize: '20px',
      fontWeight: 'bold',
      letterSpacing: '0.720px',
      fontFamily: 'Forma-DJR-Display',
    },
    sectionSubtext: {
      fontFamily: 'Untitled-Sans',
      marginBottom: '10px',
    },
  }),
);

type Props = {
  path?: string;
};

export default function MyEnergyUse(_: Props) {
  const { accountParams, encryptedAccountNumber } = useSelectedAccountParams();
  const [_2, setEtData] = useState<
    | {
        getAccountDetails: Array<AccountDetail>;
      }
    | undefined
  >();
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const { accountTypeData } = useGetAccountType({});
  const { customer, loading: customerLoading } = useAccountCustomer();
  const isMobile = useIsMobile();

  const { data: account } = useAuthQuery<{
    getAccountDetails: [AccountDetail];
  }>(GET_ACCOUNT_DETAILS, {
    variables: {
      params: {
        accountNumberList: [accountParams],
      },
    },
    skip: !accountParams,
    errorPolicy: 'all',
  });

  const encryptedServiceAgreementId = getActiveEncryptedSaId(
    account?.getAccountDetails?.[0]?.premiseInfo as PremiseInfo[] | undefined,
  );

  const { loading: eTLoading, data: etData } = useAuthQuery<{
    getAccountDetails: Array<AccountDetail>;
  }>(getPgeEnergyTrackerData, {
    variables: {
      params: {
        accountNumberList: [accountParams],
      },
    },
    skip: !accountParams,
  });

  const { data: timeOfDayData, loading: todStatusLoading } = useAuthQuery<{
    getAccountDetails: Array<AccountDetail>;
  }>(GET_TOD_ENROLLMENT_STATUS_QUERY, {
    variables: {
      params: {
        accountNumberList: [accountParams],
      },
    },
    errorPolicy: 'all',
    skip: !accountParams,
  });

  useEffect(() => {
    setEtData?.(etData);
  }, [etData]);

  const pgeEnergyTrackerData = etData?.getAccountDetails?.[0]?.pgeEnergyTracker;
  const isResAccount =
    accountTypeData?.getAccountDetails[0]?.accountType === AccountType?.Res;
  const isLoading = etData === undefined || eTLoading || todStatusLoading;
  const isWidgetNotAvailable =
    pgeEnergyTrackerData?.energyTrackerInfo?.identifiers?.length === 0 ||
    !Boolean(pgeEnergyTrackerData?.energyTrackerInfo?.identifiers);

  if (isLoading || accountTypeData === undefined) {
    return <Backdrop forceOpen message={t('STILL_LOADING')} />;
  }
  return (
    <Fragment>
      {isLoading || accountTypeData === undefined ? (
        <Backdrop forceOpen message={t('STILL_LOADING')} />
      ) : (
        <>
          <Typography className={classes.heading}>
            {t('MY_ENERGY_USE')}
          </Typography>
          <Box className={classes.accountBox}>
            <AccountDropdown />
          </Box>
          {isResAccount ? (
            <>
              <Box>
                <Typography className={classes.sectionTitle}>
                  {t('MY_USE_2')}
                </Typography>
                {timeOfDayData?.getAccountDetails?.[0]?.timeOfDayInfo
                  ?.enrollmentStatus === TimeOfDayEnrollmentStatus.Enrolled ? (
                  <Typography className={classes.sectionSubtext}>
                    {richT('MY_USE_TEXT')}
                  </Typography>
                ) : (
                  <Typography className={classes.sectionSubtext}>
                    {t('MY_USE_SUBTEXT')}
                  </Typography>
                )}
                <Card className={classes.colorTextPrimary}>
                  <CardContent
                    className={classes.widget}
                    style={{
                      backgroundColor: 'unset',
                      margin: 'unset',
                    }}
                  >
                    {isWidgetNotAvailable ? (
                      <Typography>
                        {t('CURRENTLY_NOT_AVAILABLE_FOR_THIS_ACCOUNT')}
                      </Typography>
                    ) : (
                      !customerLoading && (
                        <WidgetHost
                          key={accountParams?.accountNumber ?? ''}
                          type="energy-usage"
                          identifier={accountParams?.accountNumber ?? ''}
                          personId={accountParams?.encryptedPersonId ?? ''}
                          isMobile={isMobile}
                          displayEnergyUsageDownloadButton={true}
                        />
                      )
                    )}
                  </CardContent>
                </Card>
              </Box>
            </>
          ) : (
            !isResAccount && (
              <Box>
                <Typography className={classes.sectionTitle}>
                  {t('MY_USE')}
                </Typography>
                <Typography className={classes.sectionSubtext}>
                  {t('MY_USE_SUBTEXT_2')}
                </Typography>
                <Card className={classes.colorTextPrimary}>
                  <CardContent
                    className={classes.widget}
                    style={{
                      backgroundColor: 'unset',
                      margin: 'unset',
                    }}
                  >
                    {isWidgetNotAvailable ? (
                      <Typography>
                        {t('CURRENTLY_NOT_AVAILABLE_FOR_THIS_ACCOUNT')}
                      </Typography>
                    ) : (
                      !customerLoading && (
                        <WidgetHost
                          key={accountParams?.accountNumber ?? ''}
                          type="energy-usage"
                          identifier={accountParams?.accountNumber ?? ''}
                          personId={accountParams?.encryptedPersonId ?? ''}
                          isMobile={isMobile}
                          displayEnergyUsageDownloadButton={true}
                        />
                      )
                    )}
                  </CardContent>
                </Card>
              </Box>
            )
          )}
        </>
      )}
    </Fragment>
  );
}
